import {
  BIOTOPAUSGLEICH_LABEL,
  EMPTY_STATEMENT,
  biotopausgleichKeys,
  isBiotopausgleichKey,
  mergeStatement,
  STATEMENT_KEYS,
  STATEMENT_UNITS,
  RequestStatementSchema,
  RequestStatementToolSchema,
  normaliseStatement,
  parseStoredStatement,
  statementKeyForProductType,
  type NamedStatementKey,
  type RequestStatement,
  type RequestStatementInput,
  type StatementEntry,
  type StatementKey,
} from "@/lib/requests/axis-hints";
import { namesBothDenominations } from "@/lib/requests/funnel-statement";
import { formatQuantityWithUnitKey } from "@/lib/formatting";

/**
 * What the SELLER states in the Schnellangebot (ARCH-039 D13/D16, WP8).
 *
 * The same six entries as the buyer's statement, in the same shape, with the same units and the
 * same exclusivity of „ich bin mir nicht sicher" — because a statement is a statement: the
 * customer names what they have, nothing is inferred (D1), nothing is summed. Every primitive is
 * the buyer module's, imported rather than copied; the database says the same thing
 * (`is_offering_statement` delegates to `is_request_statement`).
 *
 * What is genuinely the seller's own lives here: the words the seller reads ("Erstaufforstungs-
 * fläche", not "Ersatzaufforstung"), and the parent facts a supply statement derives — the
 * product type, its quantity, and D17's `naturschutz_supply`, which is false exactly when the
 * seller offers no Ökopunkte and no hectares.
 */
export type OfferingStatement = RequestStatement;
export type OfferingStatementInput = RequestStatementInput;
export const OfferingStatementSchema = RequestStatementSchema;
export const EMPTY_OFFERING_STATEMENT: OfferingStatement = EMPTY_STATEMENT;

/**
 * What a seller said about their supply in one turn of the assistant's chat, folded into what they
 * stated before: the entries the turn names replace the stored ones, the rest stay as stated
 * (mergeStatement, the buyer's rule).
 */
export function mergeOfferingStatementTurn(
  stored: unknown,
  turn: OfferingStatementInput
): OfferingStatement | null {
  return mergeStatement(parseStoredOfferingStatement(stored), turn);
}

const NAMED_KEYS = STATEMENT_KEYS.filter((key): key is NamedStatementKey => key !== "unknown");

/**
 * A turn folded into the statement of a draft the assistant reuses, which takes only the answers it
 * does not have yet: a supply the statement does not state, and an amount or words for one it
 * states without them. What it states stays as stated. The other denomination of a Biotopausgleich
 * it states is no gap: one Fläche is one uplift.
 */
export function fillOfferingStatementGaps(
  stored: unknown,
  turn: OfferingStatementInput
): OfferingStatement | null {
  const current = parseStoredOfferingStatement(stored);
  if (current === null) return mergeStatement(null, turn);
  const held = biotopausgleichKeys(current);
  const gaps: OfferingStatementInput = {};
  for (const key of NAMED_KEYS) {
    const entry = turn[key] as Partial<StatementEntry> | null | undefined;
    if (!entry) continue;
    if (isBiotopausgleichKey(key) && held.length > 0 && !held.includes(key)) continue;
    const was = current[key] as Partial<StatementEntry>;
    if (!was.selected) {
      gaps[key] = entry;
      continue;
    }
    const fill = {
      ...(was.quantity == null && typeof entry.quantity === "number"
        ? { quantity: entry.quantity }
        : {}),
      ...(!was.details?.trim() && entry.details?.trim() ? { details: entry.details } : {}),
    };
    if (Object.keys(fill).length > 0) gaps[key] = fill;
  }
  return Object.keys(gaps).length > 0 ? mergeStatement(current, gaps) : current;
}

/** Biotopausgleich is one supply in two denominations; the seller names one, as the buyer does. */
export const BIOTOPAUSGLEICH_SUPPLY_LABEL = BIOTOPAUSGLEICH_LABEL;

/**
 * `label` is what the seller reads, `adminLabel` what Sales reads on the lead. The forst axis is
 * named for what the seller HAS (a Fläche they could afforest), not for the buyer's obligation
 * (an Ersatzaufforstung they owe); the two subject rows carry the seller's own words, because
 * which species or which element it is decides everything and only the seller knows.
 */
export const OFFERING_STATEMENT_OPTIONS: Record<
  StatementKey,
  { label: string; adminLabel: string; detailsPlaceholder?: string; helper?: string }
> = {
  naturschutz_points: { label: "Ökopunkte", adminLabel: "Ökopunkte" },
  naturschutz_area: { label: "Ausgleichsfläche", adminLabel: "Ausgleichsfläche" },
  forst: {
    label: "Erstaufforstungsfläche",
    adminLabel: "Erstaufforstungsfläche",
    detailsPlaceholder: "z. B. Lage, Bodengüte oder geplante Baumarten",
  },
  artenschutz: {
    label: "Artenschutz",
    adminLabel: "Artenschutz",
    detailsPlaceholder: "z. B. Feldlerche, Kiebitz",
  },
  biotop: {
    label: "Sonderbiotop (Knick, Wallhecke, Allee)",
    adminLabel: "Sonderbiotop",
    detailsPlaceholder: "z. B. Knick, Wallhecke",
  },
  unknown: {
    label: "Ich bin mir nicht sicher – bitte rufen Sie mich an",
    adminLabel: "weiß nicht",
    helper: "Wir melden uns telefonisch bei Ihnen — kostenfrei und unverbindlich.",
  },
};

/**
 * The parent offering's own facts, derived from the statement. Ökopunkte are the parent when
 * offered; otherwise Ausgleichsfläche; otherwise the offering carries no point-or-hectare supply
 * at all — it is D17's carrier: type stays the market's vocabulary, the quantity is unknown, and
 * `naturschutz_supply` false keeps it out of every parent-grain reader while its native lines
 * match at line grain. The database CHECK `offerings_carrier_has_no_quantity` is the same rule.
 */
export function parentFromOfferingStatement(statement: OfferingStatement | null): {
  type: "okopunkte" | "ausgleichsflaechen";
  quantity: number | null;
  naturschutz_supply: boolean;
} {
  if (statement?.naturschutz_points.selected)
    return {
      type: "okopunkte",
      quantity: statement.naturschutz_points.quantity ?? null,
      naturschutz_supply: true,
    };
  if (statement?.naturschutz_area.selected)
    return {
      type: "ausgleichsflaechen",
      quantity: statement.naturschutz_area.quantity ?? null,
      naturschutz_supply: true,
    };
  return { type: "okopunkte", quantity: null, naturschutz_supply: false };
}

/**
 * The statement that says what the parent now says — the inverse of `parentFromOfferingStatement`.
 *
 * Sales edits the parent directly. Left alone, the seller's stored statement would keep the old
 * denomination, and their next autosave would re-derive it and flip the parent back. So the
 * parent's own denomination moves in the same write: the selection and the amount go to the key
 * for the new unit, a carrier keeps neither, and every other entry stays the seller's. „Ich bin
 * mir nicht sicher" yields to a named position — `normaliseStatement` would otherwise keep it
 * and drop the key just set.
 */
export function offeringStatementForParent(
  stored: OfferingStatement,
  parent: ReturnType<typeof parentFromOfferingStatement>
): OfferingStatement | null {
  const key = statementKeyForProductType(parent.type);
  const cleared = {
    ...stored,
    naturschutz_points: { selected: false },
    naturschutz_area: { selected: false },
  };
  const next = normaliseStatement(
    parent.naturschutz_supply
      ? {
          ...cleared,
          unknown: { selected: false },
          [key]: {
            selected: true,
            quantity: parent.quantity ?? undefined,
            unit: STATEMENT_UNITS[key][0],
          },
        }
      : cleared
  );
  return offeringStatementSelectedKeys(next).length === 0 ? null : next;
}

/** A price is asked for — and can only be denominated — where a point or hectare supply is named. */
export function offeringStatementTakesPrice(statement: OfferingStatement | null): boolean {
  return parentFromOfferingStatement(statement).naturschutz_supply;
}

export const OFFERING_STATEMENT_DENOMINATION_CONFLICT_CODE = "STATEMENT_DENOMINATION_CONFLICT";
export const OFFERING_STATEMENT_DENOMINATION_CONFLICT_MESSAGE =
  "Biotopausgleich wird entweder in Ökopunkten oder als Ausgleichsfläche in Hektar angeboten. Bitte wählen Sie eine Einheit.";

/** The seller states ONE denomination of Biotopausgleich, as the buyer does (funnel-only). */
export const FunnelOfferingStatementSchema = OfferingStatementSchema.refine(
  (statement) => !namesBothDenominations(statement),
  { message: OFFERING_STATEMENT_DENOMINATION_CONFLICT_MESSAGE, path: ["naturschutz_area"] }
);

/** Whether a submitted value breaks the funnel's rule and nothing else (see funnel-statement.ts). */
export function violatesFunnelOfferingStatement(axisStatement: unknown): boolean {
  return (
    !FunnelOfferingStatementSchema.safeParse(axisStatement).success &&
    OfferingStatementSchema.safeParse(axisStatement).success
  );
}

/** Narrow a statement to the funnel's reading: the denomination the parent facts come from. */
export function toFunnelOfferingStatement(
  statement: OfferingStatement | null
): OfferingStatement | null {
  if (!namesBothDenominations(statement) || !statement) return statement;
  return { ...statement, naturschutz_area: EMPTY_STATEMENT.naturschutz_area };
}

/** Sales' one-line rendering of a seller lead. */
export function formatOfferingStatement(statement?: OfferingStatement | null): string {
  if (!statement) return "keine Angabe";
  if (statement.unknown.selected) return OFFERING_STATEMENT_OPTIONS.unknown.adminLabel;
  const parts = NAMED_KEYS.filter((key) => statement[key].selected).map((key) => {
    const entry = statement[key];
    const label = OFFERING_STATEMENT_OPTIONS[key].adminLabel;
    if (entry.quantity == null) return label;
    return `${label} ${formatQuantityWithUnitKey(entry.quantity, entry.unit ?? STATEMENT_UNITS[key][0])}`;
  });
  return parts.length ? parts.join(" · ") : "keine Angabe";
}

/** „Ich bin mir nicht sicher" is the seller funnel's callback request, and Sales reads it first. */
export const OFFERING_CALLBACK_REQUESTED_NOTE = "Rückruf gewünscht (Schnellangebot)";

/** A stored statement as the one line Sales reads on a seller lead; null when nothing was stated. */
export function describeStoredOfferingStatement(value: unknown): string | null {
  const statement = parseStoredOfferingStatement(value);
  if (!statement) return null;
  return statement.unknown.selected
    ? OFFERING_CALLBACK_REQUESTED_NOTE
    : formatOfferingStatement(statement);
}

/** A value read back from the database: anything unreadable is no answer. */
export function parseStoredOfferingStatement(value: unknown): OfferingStatement | null {
  return parseStoredStatement(value);
}

/**
 * Chat input: the buyer tool's shape with the seller's words. The shape is identical — four
 * supplies, two denominations of one of them, text instead of a number where the unit of supply
 * is a species or an element — so the schema is reused and only its description is the seller's.
 */
export const OfferingStatementToolSchema = RequestStatementToolSchema.describe(
  "Angebot des Verkäufers, nur was er ausdrücklich nennt; eine Verneinung weglassen. Biotopausgleich in genau EINER Einheit: naturschutz_points (Ökopunkte) ODER naturschutz_area (Ausgleichsfläche in ha) — nennt der Verkäufer beides, frage, welche gilt. forst = Erstaufforstungsfläche (in ha). artenschutz und biotop (Sonderbiotop, z. B. Knick, Wallhecke, Allee) haben keine Menge; details (auch bei forst) nimmt seine Worte wörtlich auf und darf fehlen. Menge nur übernehmen, wenn der Verkäufer sie genannt hat, und niemals umrechnen; erfrage sie nur, wenn das Tool sie als fehlende Angabe meldet. unknown: {selected: true} bei ausdrücklicher Unsicherheit — das ist zugleich die Bitte um einen Rückruf."
);

export const CHAT_OFFERING_STATEMENT_REFUSAL_MESSAGES = {
  CHAT_STATEMENT_REQUIRED: "Bitte geben Sie an, was angeboten wird.",
  CHAT_DENOMINATION_CONFLICT:
    "Biotopausgleich wird entweder in Ökopunkten oder als Ausgleichsfläche in Hektar angeboten. Frage den Nutzer, welche Einheit gilt, und rufe das Tool erneut mit nur einer der beiden Angaben auf.",
  CHAT_QUANTITY_WITHOUT_SUPPLY:
    "Eine Menge gehört zu Ökopunkten oder zu einer Ausgleichsfläche. Frage den Nutzer, worauf sich die Menge bezieht, und rufe das Tool erneut auf.",
} as const;
export type ChatOfferingStatementRefusalCode =
  keyof typeof CHAT_OFFERING_STATEMENT_REFUSAL_MESSAGES;

/**
 * Pure chat input normalisation for the seller tool. `type` is Klara's product routing and still
 * wins where the seller named no denomination themselves; a quantity without a point-or-hectare
 * supply names nothing the parent could hold, so it is refused rather than dropped.
 */
export function normaliseChatOfferingStatementTurn(input: {
  turn?: OfferingStatementInput;
  type?: "okopunkte" | "ausgleichsflaechen";
  quantity?: number;
}) {
  const { turn, type, quantity } = input;
  const incomingStatement = normaliseStatement(turn);
  let refusal: ChatOfferingStatementRefusalCode | undefined;
  if (!type && !incomingStatement) refusal = "CHAT_STATEMENT_REQUIRED";
  else if (namesBothDenominations(incomingStatement)) refusal = "CHAT_DENOMINATION_CONFLICT";
  // Which denomination a scalar belongs to: the seller's own words first, Klara's routing second.
  const named = parentFromOfferingStatement(incomingStatement);
  const key = named.naturschutz_supply
    ? statementKeyForProductType(named.type)
    : type
      ? statementKeyForProductType(type)
      : null;
  const statement = key
    ? normaliseStatement({
        ...(incomingStatement ?? EMPTY_STATEMENT),
        unknown: { selected: false },
        [key]: {
          ...(incomingStatement?.[key] ?? {}),
          selected: true,
          quantity: incomingStatement?.[key].quantity ?? quantity ?? null,
        },
      })
    : incomingStatement;
  const parent = parentFromOfferingStatement(statement);
  if (!refusal && quantity !== undefined && !parent.naturschutz_supply)
    refusal = "CHAT_QUANTITY_WITHOUT_SUPPLY";
  return { incomingStatement, statement, parent, refusal };
}

/** The selected entries, for analytics and tests — keys only, never amounts. */
export function offeringStatementSelectedKeys(
  statement?: OfferingStatement | null
): StatementKey[] {
  return statement ? STATEMENT_KEYS.filter((key) => statement[key].selected) : [];
}
